import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  FormControl,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  VStack,
  useToast,
} from '@chakra-ui/react';
import {
  UpdateBranchRegionRequest,
  branchRegionsApi,
} from '@texas/api/endpoints/branchRegionsApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { ErrorLabel } from '@texas/components/shared/ErrorLabel';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { SubmitButton } from '@texas/components/shared/form/SubmitButton';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';
import { HookFormCounter } from '@texas/components/shared/form/HookFormCounter';
import { ReactSelectOption } from '@texas/types';
import { request } from '@texas/utils/helpers/httpHelpers';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useEffect } from 'react';
import { SharedDisclosureProps } from '@texas/components/shared/types';
import { TexasSelect } from '@texas/components/shared/form/TexasSelect';
import { SpinnerWithLabel } from '@texas/components/shared/SpinnerWithLabel';
import { useMetadata } from '@texas/hooks/metadata/useMetadata';

export interface EditBranchRegionProps extends SharedDisclosureProps {
  branchRegionId: number;
  onUpdated: () => void;
}

export function EditBranchRegion({
  branchRegionId,
  onUpdated,
  onClose,
  isOpen,
}: EditBranchRegionProps) {
  const { t } = useTranslation();

  return (
    <Modal
      variant="entityDirectory"
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader>{t('branchRegion.edit')}</ModalHeader>
        <Inner
          branchRegionId={branchRegionId}
          onClose={onClose}
          onUpdated={onUpdated}
        />
      </ModalContent>
    </Modal>
  );
}

function Inner({
  branchRegionId,
  onClose,
  onUpdated,
}: {
  branchRegionId: number;
  onClose: () => void;
  onUpdated: () => void;
}) {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<UpdateBranchRegionRequest>();

  const toast = useToast();
  const { t } = useTranslation();
  const { request: updateBranchRegionRequest, error } = useApiRequest(
    branchRegionsApi.update,
  );

  const {
    data: branchRegion,
    refetch: refetchBranchRegion,
    loading: branchRegionLoading,
  } = useApiResource(branchRegionsApi.get);

  const {
    branchesData: [branches, getBranches, loadingBranches],
  } = useMetadata();

  useEffect(() => {
    refetchBranchRegion(branchRegionId);
    getBranches();
  }, [branchRegionId, refetchBranchRegion, getBranches]);

  useEffect(() => {
    if (branchRegion) {
      reset({
        name: branchRegion.name,
        branchIds: branchRegion.branches.map((b) => b.id),
      });
    }
  }, [branchRegion, reset]);

  const onSubmit = async (data: UpdateBranchRegionRequest) =>
    await request(updateBranchRegionRequest, [branchRegionId, data], () => {
      toast({
        title: t('general.updated'),
        status: 'success',
        isClosable: true,
      });
      onUpdated();
      onClose();
    });

  return (
    <>
      {error && <ErrorDetails error={error} />}
      {branchRegionLoading && <SpinnerWithLabel label={t('general.loading')} />}
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired={true} isInvalid={!!errors.name}>
              <TexasFormLabel>{t('general.name')}</TexasFormLabel>
              <Input
                variant="entityDirectory"
                {...register('name', {
                  required: t('errors.nameRequired', {
                    name: t('general.name'),
                  }),
                  maxLength: {
                    value: 100,
                    message: t('errors.nameMaxLength', {
                      name: t('general.name'),
                      count: 100,
                    }),
                  },
                })}
              />
              <HookFormCounter control={control} name="name" maxLength={100} />
              <ErrorLabel text={errors.name?.message} />
            </FormControl>

            <FormControl>
              <TexasFormLabel>{t('general.branches')}</TexasFormLabel>
              <Controller
                name="branchIds"
                control={control}
                render={({ field }) => (
                  <TexasSelect
                    {...field}
                    isLoading={loadingBranches}
                    isClearable={true}
                    value={field.value?.map<ReactSelectOption>((x) => ({
                      label: branches.find((b) => b.id === x)?.name ?? '',
                      value: x,
                    }))}
                    isMulti={true}
                    onChange={(e) => field.onChange(e.map((x) => x.value))}
                    options={branches.map<ReactSelectOption>((x) => ({
                      value: x.id,
                      label: `${x.name} (${x.identifier})`,
                    }))}
                    closeMenuOnSelect={false}
                  />
                )}
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <SubmitButton loading={isSubmitting}>
            {t('general.update')}
          </SubmitButton>
        </ModalFooter>
      </form>
    </>
  );
}
